import React, { useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import appInsights from "../../AppInsights";

const DateRangeFilter = ({ startDate, endDate, setStartDate, setEndDate }) => {
  // Track component load
  useEffect(() => {
    const openTime = new Date().toLocaleString();
    appInsights.trackEvent({
      name: "DateRangeFilter_Component_Loaded",
      properties: {
        openedAt: openTime,
        initialStartDate: startDate?.toISOString(),
        initialEndDate: endDate?.toISOString(),
        component: "Date_Range_Filter",
      },
    });
  }, []);

  // Function to get first day of current month in local time
  const getFirstDayOfMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  // Function to get last day of current month in local time
  const getLastDayOfMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  };

  // Convert to local date without time component
  const toLocalDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  // Convert to display date (handles timezone offset)
  const getDisplayDate = (date) => {
    if (!date) return null;
    return toLocalDate(date);
  };

  // Track date range changes
  useEffect(() => {
    if (startDate || endDate) {
      appInsights.trackEvent({
        name: "DateRangeFilter_Date_Range_Updated",
        properties: {
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          hasStartDate: !!startDate,
          hasEndDate: !!endDate,
          dateRangeLength: startDate && endDate ? 
            Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) : 0,
          isCurrentMonth: startDate && endDate ? 
            startDate.getMonth() === new Date().getMonth() && 
            endDate.getMonth() === new Date().getMonth() : false,
        },
      });
    }
  }, [startDate, endDate]);

  // Handle start date change
  const handleStartDateChange = (date) => {
    if (!date) return;

    const localDate = toLocalDate(date);
    
    // Track start date selection
    appInsights.trackEvent({
      name: "DateRangeFilter_Start_Date_Selected",
      properties: {
        selectedDate: localDate.toISOString(),
        previousStartDate: startDate?.toISOString(),
        hasEndDate: !!endDate,
        endDate: endDate?.toISOString(),
        action: "start_date_selection",
      },
    });

    setStartDate(localDate);

    // Reset end date if it's before the new start date
    if (endDate && localDate > endDate) {
      // Track automatic end date reset
      appInsights.trackEvent({
        name: "DateRangeFilter_End_Date_Auto_Reset",
        properties: {
          reason: "start_date_after_end_date",
          newStartDate: localDate.toISOString(),
          previousEndDate: endDate?.toISOString(),
          conflict: true,
        },
      });
      setEndDate(null);
    }
  };

  // Handle end date change
  const handleEndDateChange = (date) => {
    if (!date) return;

    const localDate = toLocalDate(date);
    
    // Track end date selection attempt
    appInsights.trackEvent({
      name: "DateRangeFilter_End_Date_Selected",
      properties: {
        selectedDate: localDate.toISOString(),
        previousEndDate: endDate?.toISOString(),
        hasStartDate: !!startDate,
        startDate: startDate?.toISOString(),
        isValidSelection: !startDate || localDate >= startDate,
        action: "end_date_selection",
      },
    });

    // Only set if there's no start date or new date is after start date
    if (!startDate || localDate >= startDate) {
      setEndDate(localDate);
    } else {
      // Track invalid end date selection
      appInsights.trackEvent({
        name: "DateRangeFilter_Invalid_End_Date",
        properties: {
          selectedEndDate: localDate.toISOString(),
          currentStartDate: startDate?.toISOString(),
          validationError: "end_date_before_start_date",
          daysDifference: Math.ceil((startDate - localDate) / (1000 * 60 * 60 * 24)),
        },
      });
    }
  };

  // Track date picker interactions
  const handleDatePickerOpen = (type) => {
    appInsights.trackEvent({
      name: "DateRangeFilter_DatePicker_Opened",
      properties: {
        pickerType: type,
        currentStartDate: startDate?.toISOString(),
        currentEndDate: endDate?.toISOString(),
      },
    });
  };

  // Track current month boundaries
  useEffect(() => {
    const firstDay = getFirstDayOfMonth();
    const lastDay = getLastDayOfMonth();
    
    appInsights.trackEvent({
      name: "DateRangeFilter_Month_Boundaries",
      properties: {
        firstDayOfMonth: firstDay.toISOString(),
        lastDayOfMonth: lastDay.toISOString(),
        currentMonth: firstDay.toLocaleString('default', { month: 'long' }),
        currentYear: firstDay.getFullYear(),
      },
    });
  }, []);

  return (
    <div className="date-range-filter ">
      <span className="filter-date-label">From:</span>
      <span className="date-picker">
        <DatePicker
          selected={getDisplayDate(startDate)}
          onChange={handleStartDateChange}
          onCalendarOpen={() => handleDatePickerOpen("start")}
          maxDate={endDate || getLastDayOfMonth()}
          dateFormat="MMM d, yyyy"
          placeholderText="Start Date"
          className="custom-datepicker"
          dayClassName={(date) => {
            const selectedDate = getDisplayDate(startDate);
            return selectedDate &&
              date.toDateString() === selectedDate.toDateString()
              ? "inline-selected-day"
              : undefined;
          }}
        />

        <style>
          {`
         .inline-selected-day {
          background-color: #383D8D !important; 
          color: white !important;
         }
     
         `}
        </style>

        <i className="bi bi-calendar3 calendar-icon"></i>
      </span>

      <span className="filter-date-label">To:</span>
      <span className="date-picker">
        <DatePicker
          selected={getDisplayDate(endDate)}
          onChange={handleEndDateChange}
          onCalendarOpen={() => handleDatePickerOpen("end")}
          minDate={startDate || getFirstDayOfMonth()}
          maxDate={getLastDayOfMonth()}
          dateFormat="MMM d, yyyy"
          placeholderText="End Date"
          className="custom-datepicker"
          dayClassName={(date) => {
            const selectedDate = getDisplayDate(endDate);
            return selectedDate &&
              date.toDateString() === selectedDate.toDateString()
              ? "inline-selected-day"
              : undefined;
          }}
        />

        <style>
          {`
    .inline-selected-day {
      background-color: #383D8D !important; 
      color: white !important;
    }
   
  `}
        </style>

        <i className="bi bi-calendar3 calendar-icon"></i>
      </span>
    </div>
  );
};

export default DateRangeFilter;